import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { redirect } from 'saddlebag-browser';

import type {
  RequestInit,
  RequestInitHeaders,
} from '@web-platform/shared-universal-types/src/network/internals/types';

type ResolvedResponse = {
  json: any;
  status: number;
  statusText: string;
  ok: boolean;
  headers: RequestInitHeaders;
};

const BOOKING_EXPIRED_MESSAGES = [
  'Session not found in state',
  'Itinerary not found in state',
  'Cache not found for session',
];

const LOGGING_COMPONENT = 'httpFetchClient';
const RETRY_CONFIG = {
  // Retry 3 times on requests that return a response (500, etc.) before giving up.
  retry: 3,
  // Retry twice on errors that don't return a response (ENOTFOUND, ETIMEDOUT, etc).
  noResponseRetries: 2,
  retryDelay: 300,
  httpMethodsToRetry: ['GET', 'HEAD', 'OPTIONS', 'POST'],
  // The response status codes to retry.  Supports a double
  // array with a list of ranges.
  statusCodesToRetry: [
    [100, 199],
    [429, 429],
    [502, 599],
  ],
};

const extractServiceName = (url: string): string => {
  const isExploreStaticBucket = url.includes('sttc/explore-static/');
  if (isExploreStaticBucket) {
    return 'explore-static-bucket';
  }
  const serviceType = url.match(/\/g\/([^/]+)/)?.[1] || '';
  return serviceType ? String(serviceType) : 'unknown service';
};

const isConductorBookingRequestExpired = (url: string, text: string) => {
  const isConductorBookingRequest = /\/g\/conductor\/v1\/fps3\/search\/.*\/booking.*$/.test(url);
  const isExpiredError = BOOKING_EXPIRED_MESSAGES.some((message) => text?.includes(message));
  return isConductorBookingRequest && isExpiredError;
};

const handleBotTraffic = async (status: number, data: any) => {
  if (status === 403 && data && data.redirect_to) {
    const { redirect_to: redirectTo } = data;

    redirect(redirectTo);
    return true;
  }
  return false;
};

const handleErrorResponse = async (
  requestInit: RequestInit,
  resolvedResponse: ResolvedResponse,
) => {
  const { headers, json, status, statusText } = resolvedResponse;
  const { url } = requestInit;

  // Check if bot, and redirect to captcha if so
  const isBot = await handleBotTraffic(status, json);

  // We avoid sending error logs if the error request comes from a bot
  if (!isBot) {
    if (status === 404 && isConductorBookingRequestExpired(url, JSON.stringify(json))) {
      logWarn({
        component: 'http-conductor-booking',
        message: `response failed due to conductor booking request expired`,
        apiUrl: url,
      });
    } else {
      logWarn({
        component: LOGGING_COMPONENT,
        error: statusText,
        message: 'Request failed',
        responseBody: JSON.stringify(json),
        requestBody: requestInit.body ? JSON.stringify(requestInit.body) : 'Body is undefined',
        requestHeaders: JSON.stringify(requestInit.headers),
        status: status.toString(),
        statusText,
        url,
      });
    }
  }

  return {
    error: true,
    headers,
    body: json,
    status,
    statusText,
  };
};

const statusCodeShouldBeRetried = (statusCode: number) =>
  RETRY_CONFIG.statusCodesToRetry.reduce(
    (prevShouldRetry: boolean, statusCodes: number[]) =>
      prevShouldRetry || (statusCodes[0] <= statusCode && statusCodes[1] >= statusCode),
    false,
  );

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
const requestMethodShouldBeRetried = (requestMethod: string) =>
  RETRY_CONFIG.httpMethodsToRetry.includes(requestMethod.toUpperCase());

const fetchWithRetries = async (
  requestInit: RequestInit,
  retryConfig = RETRY_CONFIG,
  retryCount = 0,
): Promise<ResolvedResponse> => {
  let resolvedResponse;
  let request;

  try {
    const { body, headers, method, omitCookie, url } = requestInit;
    request = new Request(url, {
      method,
      body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', ...headers },
      credentials: omitCookie ? 'omit' : 'same-origin',
    });
    const fetchResp = await fetch(request);

    // We resolve the response here as fetch can throw network errors when resolving the response
    resolvedResponse = {
      json: await fetchResp.json(),
      status: fetchResp.status,
      statusText: fetchResp.statusText,
      ok: fetchResp.ok,
      headers: Object.fromEntries(fetchResp.headers.entries()),
    };
  } catch (err) {
    const methodShouldBeRetried = requestMethodShouldBeRetried(requestInit.method);

    // We did not get a response due to an error - retry?
    if (retryCount < retryConfig.noResponseRetries && methodShouldBeRetried) {
      await delay(retryConfig.retryDelay);
      return fetchWithRetries(requestInit, retryConfig, retryCount + 1);
    }

    throw err;
  }

  const shouldRetry =
    !resolvedResponse.ok &&
    retryCount < retryConfig.retry &&
    statusCodeShouldBeRetried(resolvedResponse.status) &&
    requestMethodShouldBeRetried(requestInit.method);

  if (shouldRetry) {
    await delay(retryConfig.retryDelay);
    return fetchWithRetries(requestInit, retryConfig, retryCount + 1);
  }
  return resolvedResponse;
};

export const http = async (requestInit: RequestInit): Promise<any> => {
  let resolvedResponse: ResolvedResponse;

  try {
    resolvedResponse = await fetchWithRetries(requestInit);
  } catch (error) {
    logWarn({
      component: LOGGING_COMPONENT,
      error: error.toString(),
      message: `Error in network request to ${extractServiceName(requestInit.url)}. Retried ${RETRY_CONFIG.noResponseRetries} times, will not retry again.`,
      url: requestInit.url,
      httpMethod: requestInit.method,
      stack: error.stack,
    });
    throw error;
  }

  const { headers, json, ok, status, statusText } = resolvedResponse;

  if (!ok) {
    return handleErrorResponse(requestInit, resolvedResponse);
  }

  return {
    error: false,
    headers,
    body: json,
    status,
    statusText,
  };
};
